import { useQueryClient } from '@tanstack/react-query';
import type { SubmitHandler } from 'react-hook-form';

import { useTickets } from 'contexts/TicketsContext';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';
import type { ApiErrorI } from 'declarations/interfaces/errors';
import type { NoopT } from 'declarations/types/common';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useImportSchedule } from 'hooks/api/useTickets';
import { updateSnackbarNotification } from 'reduxStore/features/notifications/notificationsSlice';
import { useAppDispatch } from 'reduxStore/hooks';

type ImportScheduleT = {
  isImportSchedulePopupOpened: boolean;
  handleClosePopup: NoopT;
  onSubmit: SubmitHandler<{ csv: File[] | null; roofTopId: string }>;
  isUploading: boolean;
};

const useImportSchedulePopup = (): ImportScheduleT => {
  const {
    getSearchParam,
    setImportSchedulePopupVisibility,
    isImportSchedulePopupOpened,
    setSelectedRoofTop,
  } = useTickets();

  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const handleClosePopup = (): void => {
    setImportSchedulePopupVisibility(false);
    createImportScheduleMutation.reset();
    setSelectedRoofTop(getSearchParam(TicketsRefinementKeysE.RoofTopId) ?? '');
  };

  const createImportScheduleMutation = useImportSchedule({
    onSuccess: _data => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);

      let msg = 'Schedule successfully imported';
      if (_data.createdCount > 0) {
        msg = `Created: ${_data.createdCount} schedule tickets`;
      } else if (_data.updatedCount > 0) {
        msg = `Updated: ${_data.updatedCount} schedule tickets`;
      }
      dispatch(
        updateSnackbarNotification({
          snackbarMessage: msg,
          snackBarSeverity: 'success',
        })
      );
      handleClosePopup();
    },
    onError: error => {
      const errorMsg =
        getErrorMessage(error as ApiErrorI) || getValidationErrorMessage(error as ApiErrorI);
      dispatch(
        updateSnackbarNotification({
          snackbarMessage: `Something went wrong. ${errorMsg || ''}`,
          snackBarSeverity: 'error',
        })
      );
      handleClosePopup();
    },
  });

  const onSubmit: SubmitHandler<{ csv: File[] | null; roofTopId: string }> = (data): void => {
    const file = data.csv?.[0];
    if (!file || !data.roofTopId) return;
    createImportScheduleMutation.mutate({
      File: file,
      RoofTopId: data.roofTopId,
    });
  };

  return {
    isImportSchedulePopupOpened,
    handleClosePopup,
    onSubmit,
    isUploading: createImportScheduleMutation.isLoading,
  };
};

export default useImportSchedulePopup;
